import {pick} from 'lodash-es';
import {useEffect, useReducer, useState} from 'react';

import {BusinessLimitRequestValidator} from '@afriex/core/domain/entities/BusinessLimitRequestValidator';

import {businessLimitService} from '@/services/businessLimitService';
import type {BusinessLimit} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import {LimitActionType} from './types';
import updateLimitReducer from './updateLimitReducer';

const useLimitUpdate = (
  onClose: VoidFunction,
  initialData: Partial<BusinessLimit> = {
    limit: {},
    meta: {
      flags: {
        isDeactivated: false,
      },
      tags: {},
    },
  },
) => {
  const [limit, dispatch] = useReducer(
    updateLimitReducer,
    initialData as BusinessLimit,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch({
      type: LimitActionType.UPDATE_LIMIT,
      payload: initialData,
    });
  }, []);

  const handleSubmit = async (rawLimit: BusinessLimit) => {
    setIsLoading(true);
    try {
      const validLimit = new BusinessLimitRequestValidator(rawLimit).value();

      if (rawLimit.id) {
        const fieldsToInclude: (keyof BusinessLimit)[] = [
          'name',
          'limit',
          'meta',
        ];
        await businessLimitService.updateBusinessLimitById(
          String(rawLimit.id),
          pick(validLimit, fieldsToInclude),
        );
        showSuccessMessage(`Limit item updated successfully`);
      } else {
        await businessLimitService.createBusinessLimit(validLimit);
        showSuccessMessage(`Limit item saved successfully`);
      }

      onClose();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e: unknown) {
      // The shared core validator throws an AfriexError whose `.message` is the
      // error code; the human-readable detail lives in `meta.errorMessage`.
      const err = e as {meta?: {errorMessage?: string}};
      const detail = err?.meta?.errorMessage || getErrorMessage(e, String(e));
      showErrorMessage(`Error saving limit item: ${detail}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    try {
      await businessLimitService.removeBusinessLimitById(id);
      showSuccessMessage('Limit deleted');

      onClose();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e: unknown) {
      showErrorMessage(getErrorMessage(e, 'Failed to delete limit'));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    dispatch,
    handleSubmit,
    handleDelete,
    isLoading,
    limit,
  };
};

export default useLimitUpdate;
